import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { VpError } from '../../../generated/internal.graphql';
import * as VPError from '../../../VPError';
import { type InternalApiError, toApiErrors } from '../errors';
import type { VPResult } from '../../VPResult';
import { COLLECTION_METADATA_FIELDS, type CollectionArguments } from '../../collection/Api';
import * as GqlCollection from '../../collection/GqlCollection';
import * as AppConfig from './AppConfig';
import { INTERNAL_APP_CONFIG_FIELDS } from './fragments';
import { ACCOUNT_AGNOSTIC } from '../../accountContext';

/**
 * Application config administration for VenuePilot staff — the API-consumer credential registry.
 *
 * ADR-009 moved this surface off the **legacy** schema (`apiClient`, `generated/graphql`, nested
 * under `app { … }`) directly onto the internal one, never passing through AppSchema: it registers
 * credentials for every API consumer regardless of tenancy, which is exactly what should not sit in a
 * published schema. Auth is the superadmin Rails session cookie — a bearer token never satisfies the
 * gate, and it only works in the Rails-embedded admin against a same-origin `/graphql`, never the
 * standalone dev server — so everything here goes through `internalGqlApiClient`.
 *
 * A non-superadmin caller is rejected with HTTP 401 before the query is parsed, which Apollo *throws*
 * rather than returning a payload. Every function below therefore catches and classifies via
 * `toApiErrors`, so callers can branch on `isUnauthorized` and say "VenuePilot staff only" instead of
 * rendering an empty table — and so an expected authorization outcome does not reach Sentry as an
 * exception. **Nothing here throws.**
 *
 * Two result shapes, matching `Internal/Account/Api.ts`: reads resolve `VPResult`, mutations resolve
 * `MutationResult`. `saveAppConfig`'s payload carries its entity, so it needs no follow-up refetch.
 */

export type MutationResult = { success: boolean; errors: InternalApiError[] };

const INTERNAL_APP_CONFIGS_QUERY = gql`
  query InternalAppConfigs($page: Int, $limit: Int) {
    applicationConfigurations(page: $page, limit: $limit) {
      collection {
        ...InternalAppConfigFields
      }
      metadata {
        ...CollectionMetadataFields
      }
    }
  }
  ${INTERNAL_APP_CONFIG_FIELDS}
  ${COLLECTION_METADATA_FIELDS}
`;

const INTERNAL_APP_CONFIG_QUERY = gql`
  query InternalAppConfig($id: Int!) {
    applicationConfiguration(id: $id) {
      ...InternalAppConfigFields
    }
  }
  ${INTERNAL_APP_CONFIG_FIELDS}
`;

const INTERNAL_SAVE_APP_CONFIG_MUTATION = gql`
  mutation InternalSaveAppConfig($appConfig: AppConfigAttributes!) {
    saveAppConfig(input: { appConfig: $appConfig }) {
      appConfig {
        ...InternalAppConfigFields
      }
      errors {
        message
        path
      }
    }
  }
  ${INTERNAL_APP_CONFIG_FIELDS}
`;

const toErrors = (errors?: VpError[] | null): InternalApiError[] =>
  VPError.fromGqlCollection(errors ?? []);

/**
 * A failed mutation with an empty `errors` list leaves the caller nothing to render. Today's
 * resolvers always populate one — but a null payload does not, so fall back rather than report a
 * silent failure.
 */
const failed = (errors: InternalApiError[], fallback: string): MutationResult => ({
  success: false,
  errors: errors.length ? errors : [{ path: '', message: fallback }],
});

export const fetchAppConfigPage = async (
  client: ApolloClient<NormalizedCacheObject>,
  params: CollectionArguments = {},
): Promise<VPResult<GqlCollection.GqlCollection<AppConfig.AppConfigType>, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_APP_CONFIGS_QUERY,
      fetchPolicy: 'network-only',
      // Both undefined leaves the backend's `page: 1, limit: 10` defaults in force, which is what
      // the screens have always got.
      variables: { page: params.page, limit: params.limit },
      // Account-agnostic: the registry covers every API consumer regardless of tenancy, as the
      // module comment above records.
      context: ACCOUNT_AGNOSTIC,
    });

    // `applicationConfigurations` is nullable. An absent collection is a backend fault, not an empty
    // page — resolving it as one would render "no application configs" and hide the failure.
    if (!data?.applicationConfigurations) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch application configs.' }] };
    }

    return {
      success: true,
      data: GqlCollection.fromGql(data.applicationConfigurations, AppConfig.fromGqlCollection),
    };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const fetchAppConfig = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<AppConfig.AppConfigType | undefined, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_APP_CONFIG_QUERY,
      fetchPolicy: 'network-only',
      variables: { id },
      // Account-agnostic: same registry.
      context: ACCOUNT_AGNOSTIC,
    });

    // A missing config is an answer (bad id), not a failure — don't dress it up as one.
    return {
      success: true,
      data: data?.applicationConfiguration ? AppConfig.fromGql(data.applicationConfiguration) : undefined,
    };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const saveAppConfig = async (
  client: ApolloClient<NormalizedCacheObject>,
  appConfig: AppConfig.AppConfigInput,
): Promise<MutationResult & { appConfig?: AppConfig.AppConfigType }> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_SAVE_APP_CONFIG_MUTATION,
      variables: { appConfig },
      fetchPolicy: 'network-only',
      // Account-agnostic: same registry, and a created credential belongs to no account either.
      context: ACCOUNT_AGNOSTIC,
    });

    const errors = toErrors(data?.saveAppConfig?.errors);
    const saved = data?.saveAppConfig?.appConfig;

    if (errors.length || !saved) {
      return failed(errors, 'Failed to save the application config.');
    }

    return { success: true, errors: [], appConfig: AppConfig.fromGql(saved) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};
